import React from 'react';
import { useCallState } from '../../contexts/CallProvider';
import { useMediaDevices } from '../../contexts/MediaDeviceProvider';
import Field from '../Field';
import { Box, CircularProgress, FormControlLabel, FormGroup, IconButton, InputLabel, MenuItem, Select, Stack, Tooltip } from '@mui/material';
import { theme } from '../../../../../../common/styles/theme';
import { CONFERENCE_SAVE_LEVELS, TConferenceSaveLevel } from '../../../../../../common/rest/conference/conferenceSaveLevel';
import { Wording } from '../../../ConferenceSettings/wording';
import { Text } from '../../../../../../common/components/Text/Text';
import { useParticipants } from '../../contexts/ParticipantsProvider';
import Tile from '../Tile';
import { BlurPersonIcon } from '../../../../../../../icons/BlurPersonIcon';

interface IDeviceSelectProps {
	// Jump straight to the Save dropdown — used when this modal was opened from the tray's
	// Recording indicator, whose whole point is changing this setting.
	autoOpenSaveLevel?: boolean;
	// Own camera on top (the home page's pre-call settings) — e.g. to check the background blur.
	showPreview?: boolean;
}

export const DeviceSelect = ({ autoOpenSaveLevel, showPreview }: IDeviceSelectProps = {}) => {
	const { ensureCallObject, saveLevel, setSaveLevel, canBlurBackground, backgroundBlur, backgroundBlurPending, setBackgroundBlur } = useCallState();
	// ParticipantsProvider is plain JS (the context's default is null), hence the cast
	const { localParticipant } = (useParticipants() ?? {}) as { localParticipant?: any };
	const [saveLevelOpen, setSaveLevelOpen] = React.useState(!!autoOpenSaveLevel);
	let {
		cams,
		mics,
		speakers,
		currentCam,
		setCurrentCam,
		currentMic,
		setCurrentMic,
		currentSpeaker,
		setCurrentSpeaker,
	} = useMediaDevices();

	const [isLoaderCam, setLoaderCam] = React.useState(false);
	const [isLoaderMic, setLoaderMic] = React.useState(false);
	const [isLoaderSpk, setLoaderSpk] = React.useState(false);

	React.useEffect(() => {
		const co = ensureCallObject?.();
		if (!co) return;
		const meeting = co.meetingState();
		if (meeting === 'new' || meeting === 'loading' || meeting === 'loaded') {
			co.startCamera().catch(() => {});
		}
	}, [ensureCallObject]);

	if (!currentCam) {
		currentCam = cams.find((i: any) => i.selected)?.device.deviceId || cams[0]?.device.deviceId;
	}
	if (!currentMic) {
		currentMic = mics.find((i: any) => i.selected)?.device.deviceId || mics[0]?.device.deviceId;
	}
	if (!currentSpeaker) {
		currentSpeaker = speakers.find((i: any) => i.selected)?.device.deviceId || speakers[0]?.device.deviceId;
	}

	if (!currentCam && !currentMic && !currentSpeaker) {
		return <div>Loading devices...</div>;
	}

	const onSetCurrentCam = (e: any) => {
		setLoaderCam(true);
		setCurrentCam(e.target.value)
			.then(() => {
				setLoaderCam(false);
			});
	}

	const onSetCurrentMic = (e: any) => {
		setLoaderMic(true);
		setCurrentMic(e.target.value)
			.then(() => {
				setLoaderMic(false);
			});
	}

	const onSetCurrentSpeaker = (e: any) => {
		setLoaderSpk(true);
		setCurrentSpeaker(e.target.value)
			.then(() => {
				setLoaderSpk(false);
			});
	}

	// CallProvider owns the actual persistence (enforced server-side regardless); this just
	// updates the shared local state, reused both pre-join and mid-call.
	const onSaveLevelChange = (e: any) => {
		setSaveLevel?.(e.target.value as TConferenceSaveLevel);
	}

	// CallProvider is plain JS, so saveLevel arrives untyped — pin it here for the lookups below.
	const level: TConferenceSaveLevel = saveLevel || 'video';

	return (
		<FormGroup
			sx={{
				// MUI's FormGroup is a wrapping column; Chrome then pads the device-select stack at the bottom
				flexWrap: 'nowrap',
			}}
		>

			<Stack
				className={`device-select ${
					saveLevelOpen ? 'disabled' : ''}`}
				sx={{
					transition: 'opacity .2s ease-in-out',

					'&.disabled': {
						opacity: 0.3,
					}
				}}
			>
				{showPreview && !!localParticipant &&
					<Box
						sx={{
							width: theme.spacing(40),
							maxWidth: 1,
							aspectRatio: 16/9,
							mb: theme.spacing(2),
							borderRadius: theme.spacing(.75),
							overflow: 'hidden',
							transform: 'scaleX(-1)',
						}}
					>
						<Tile
							participant={localParticipant}
							mirrored
							showAvatar
							showName={false}
						/>
					</Box>
				}

				<InputLabel
					sx={{
						mb: theme.spacing(1),
					}}
				>
					Select camera:
				</InputLabel>

				{/* the blur switch sits right of the camera select and wraps under it only when there's no room */}
				<Stack
					direction={'row'}
					alignItems={'center'}
					flexWrap={'wrap'}
					gap={theme.spacing(1, 3)}
					sx={{
						mb: theme.spacing(3),
					}}
				>
					<Select
						// label="Select camera:"
						onChange={onSetCurrentCam}
						value={currentCam}
						disabled={isLoaderCam}
						sx={{
							flex: '1 1 240px',
							minWidth: 0,
						}}
					>
						{cams.map((item: any) => {
							const { deviceId, label } = item.device;
							return (
								<MenuItem
									value={deviceId}
									key={`cam-${deviceId}`}
								>
									{label}
								</MenuItem>
							)
						})}
					</Select>

					{canBlurBackground &&
						<FormControlLabel
							labelPlacement={'start'}
							label={'Blur background'}
							control={
								<Tooltip title={backgroundBlur ? 'Background blurred' : 'Blur background'}>
									<IconButton
										aria-pressed={!!backgroundBlur}
										aria-busy={!!backgroundBlurPending}
										// not `disabled` while pending: MUI's Tooltip warns about a disabled child
										onClick={() => { if (!backgroundBlurPending) setBackgroundBlur(!backgroundBlur); }}
										sx={{
											// the same size with the icon and with the smaller spinner, so the form doesn't jump
											width: theme.spacing(5),
											height: theme.spacing(5),
											color: backgroundBlur ? theme.palette.primary.main : theme.palette.grey[500],
											transition: 'all .3s ease',
										}}
									>
										{backgroundBlurPending ? <CircularProgress size={16} /> : <BlurPersonIcon />}
									</IconButton>
								</Tooltip>
							}
							sx={{
								m: 0,
								gap: theme.spacing(1),
							}}
						/>
					}
				</Stack>

				<InputLabel
					sx={{
						mb: theme.spacing(1),
					}}
				>
					Select microphone:
				</InputLabel>

				<Select
					// label="Select microphone:"
					fullWidth
					onChange={onSetCurrentMic}
					value={currentMic}
					disabled={isLoaderMic}
					sx={{
						mb: theme.spacing(3),
					}}
				>
					{mics.map((item: any) => {
						const { deviceId, label } = item.device;
						return (
							<MenuItem
								value={deviceId}
								key={`mic-${deviceId}`}
							>
								{label}
							</MenuItem>
						)
					})}
				</Select>

	{/**
	 * Note: Safari does not support selection audio output devices
	 */}
				{speakers.length > 0 && <>
					<InputLabel
						sx={{
							mb: theme.spacing(1),
						}}
					>
						Select speakers:
					</InputLabel>

					<Select
						// label="Select speakers:"
						fullWidth
						onChange={onSetCurrentSpeaker}
						value={currentSpeaker}
						disabled={isLoaderSpk}
						sx={{
							mb: theme.spacing(3),
						}}
					>
						{speakers.map((item: any) => {
							const { deviceId, label } = item.device;
							return (
								<MenuItem
									value={deviceId}
									key={`speaker-${deviceId}`}
								>
									{label}
								</MenuItem>
							)
						})}
					</Select>
				</>}
			</Stack>

			<InputLabel
				sx={{
					mb: theme.spacing(1),
				}}
			>
				Save:
			</InputLabel>

			<Select
				fullWidth
				value={level}
				onChange={onSaveLevelChange}
				open={saveLevelOpen}
				onOpen={() => setSaveLevelOpen(true)}
				onClose={() => setSaveLevelOpen(false)}
				renderValue={(l) => Wording.SAVE_LEVEL_LABELS[l as TConferenceSaveLevel]}
				MenuProps={{
					anchorOrigin: {
						vertical: -32,
						horizontal: 'left',
					},
					transformOrigin: {
						vertical: 'bottom',
						horizontal: 'left',
					},
				}}
				sx={{
					'.MuiSelect-select': {
						fontWeight: 600 + ' !important',
					},
				}}
			>
				{CONFERENCE_SAVE_LEVELS.map(l => (
					<MenuItem
						value={l}
						key={`save-level-${l}`}
						sx={{
							height: 'auto',
						}}
					>
						<Stack>
							<Text variant={'subtitle2'}>{Wording.SAVE_LEVEL_LABELS[l]}</Text>
							<Text variant={'subtitle1'}
								sx={{
									color: theme.palette.grey[500],
									lineHeight: 1.2,
								}}
							>
								{Wording.SAVE_LEVEL_HINTS[l]}
							</Text>
						</Stack>
					</MenuItem>
				))}
			</Select>

		</FormGroup>
	);
};
